import React, { useState, useMemo, useCallback } from 'react';
import { Search, Download, X, ChevronDown, CheckCircle, XCircle, AlertTriangle, User, Calendar } from 'lucide-react';
import { useMaintenanceHistory } from '../../../../hooks/domain/maintenance/useMaintenanceHistory';
import { useEquipmentRegister } from '../../../../hooks/domain/useEquipmentRegister';
// FT-221: Removed useCrew/resolveCrewName - name now stored directly in record.performerName
import { useVessel } from '../../../../contexts/VesselContext';
import FilterTabs from '../../common/FilterTabs';
import Dropdown from '../../../common/Dropdown';
import { exportMaintenanceHistoryCSV, exportMaintenanceHistoryPDF } from '../../../../utils/maintenanceHistoryExport';
import MaintenanceCompletionReport from './MaintenanceCompletionReport';
// FT-349e: shared per-row indicator (Camera + count for photos, etc.)
import RowStatusIndicator from '../../shared/RowStatusIndicator';
// FT-189: Centralized equipment name resolution
import { getEquipmentDisplayName } from '../../../../services/equipment/utils/equipmentRow';
// FT-267 §16: unified category filter — normalises any legacy/inconsistent
// stored category shape (enum KEY 'LSA', enum VALUE 'Life Saving Appliances',
// canonical 'lsa', or free-text custom) to the canonical code so the filter
// matches consistently across ad-hoc + routine completions.
import { CATEGORY_DEFINITIONS, normaliseCategory } from '../../../../config/maintenanceRequirements-wrapper';

/**
 * MaintenanceHistoryView - Full-screen view of all maintenance history
 *
 * Following 3-row master template:
 * ROW 1: Title + Export + Close
 * ROW 2: Search + Category + Date Range
 * ROW 3: FilterTabs (All/By Equipment/By Job) + Count
 */
const MaintenanceHistoryView = ({ onBack }) => {
  // State for filters
  const [viewMode, setViewMode] = useState('all'); // 'all' | 'byEquipment' | 'byJob'
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [dateRange, setDateRange] = useState('12months'); // '30days' | '3months' | '12months' | 'all'
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  // Completion report state
  const [selectedCompletion, setSelectedCompletion] = useState(null);
  const [showCompletionReport, setShowCompletionReport] = useState(false);

  // Data hooks
  // FT-402: signOffCompletion persists signedOffStatus:'signed_off' + who/when.
  const { history, updateCompletionRecord, signOffCompletion } = useMaintenanceHistory();
  const { equipment } = useEquipmentRegister();
  // FT-221: Removed useCrew() - no longer needed for name resolution
  const { getCurrentVessel } = useVessel();

  // Get vessel name for exports
  const vessel = getCurrentVessel();
  const vesselName = vessel?.name || 'Vessel';

  // Create equipment lookup map
  const equipmentById = useMemo(() => {
    const map = new Map();
    (equipment || []).forEach(eq => {
      map.set(eq.id, eq);
    });
    return map;
  }, [equipment]);

  // FT-221: Use performerName if present (saved at write time), else 'Not recorded'
  // Do NOT use resolveCrewName() - it may fail if crew list not loaded
  const getCrewDisplayName = useCallback((crewId, record = null) => {
    // FT-221: Check for pre-resolved performerName first (new records)
    if (record?.performerName) {
      return record.performerName;
    }
    // Legacy fallback: show 'Not recorded' (no resolver, no UUID display)
    return 'Not recorded';
  }, []);

  // Helper to get equipment name from ID - returns friendly label
  const getEquipmentName = (equipmentId) => {
    if (!equipmentId) return 'Unknown Equipment';

    const eq = equipmentById.get(equipmentId);
    if (!eq) return equipmentId; // Equipment not found, show ID

    // FT-189: Use centralized equipment display name helper
    const friendlyName = getEquipmentDisplayName(eq);

    // If we got a friendly name, return it
    if (friendlyName && !friendlyName.startsWith('reg:')) {
      return friendlyName;
    }

    // If only technical ID exists, try to make it friendlier
    // Extract the equipment type from the ID (e.g., "ffe_extinguisher_control_position")
    if (equipmentId.includes(':')) {
      const parts = equipmentId.split(':');
      if (parts.length >= 3) {
        // Get the third part (type descriptor)
        const typeDescriptor = parts[2];
        // Convert snake_case to Title Case
        const friendly = typeDescriptor
          .split('_')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
        return friendly;
      }
    }

    // Last resort: return the ID
    return equipmentId;
  };

  // Format date for display
  const formatDate = (dateValue) => {
    if (!dateValue) return '-';
    const date = typeof dateValue === 'number' ? new Date(dateValue) : new Date(dateValue);
    if (isNaN(date.getTime())) return '-';
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  // Filter history by date range
  const filteredByDate = useMemo(() => {
    const now = Date.now();
    const msPerDay = 24 * 60 * 60 * 1000;

    return history.filter(record => {
      const recordDate = record.completedAt || (record.date ? new Date(record.date).getTime() : 0);
      if (!recordDate) return false;

      switch (dateRange) {
        case '30days':
          return (now - recordDate) <= 30 * msPerDay;
        case '3months':
          return (now - recordDate) <= 90 * msPerDay;
        case '12months':
          return (now - recordDate) <= 365 * msPerDay;
        case 'all':
        default:
          return true;
      }
    });
  }, [history, dateRange]);

  // Filter by category
  const filteredByCategory = useMemo(() => {
    if (categoryFilter === 'all') return filteredByDate;

    return filteredByDate.filter(record => {
      // FT-267 §16: normalise record.category through every legacy shape
      // (enum KEY, enum VALUE, canonical, free-text) to a canonical code so
      // the comparison against the filter (also canonical) succeeds for all
      // four. Without this, AddJobView's enum-VALUE records (e.g. 'Life
      // Saving Appliances') silently miss the filter.
      const recordCategoryCode = normaliseCategory(record.category);
      if (recordCategoryCode === categoryFilter) return true;

      // Check linked equipment categories — same normalisation applied so the
      // equipment fallback is consistent with the record predicate above.
      if (record.equipmentIds) {
        return record.equipmentIds.some(eqId => {
          const eq = equipmentById.get(eqId);
          return normaliseCategory(eq?.category) === categoryFilter;
        });
      }

      return false;
    });
  }, [filteredByDate, categoryFilter, equipmentById]);

  // Filter by search term
  const filteredBySearch = useMemo(() => {
    if (!searchTerm.trim()) return filteredByCategory;

    const term = searchTerm.toLowerCase();
    return filteredByCategory.filter(record => {
      // Search in title
      if (record.title?.toLowerCase().includes(term)) return true;
      // FT-267: belt-and-braces — also match against the ad-hoc free-text field
      // directly, so search keeps working even if the title mirror is bypassed.
      if ((record.adhocItem || '').toLowerCase().includes(term)) return true;
      // Search in equipment type
      if (record.equipmentType?.toLowerCase().includes(term)) return true;
      // Search in performer name (FT-221: pass record for performerName check)
      const performer = getCrewDisplayName(record.performedBy || record.inspector, record);
      if (performer.toLowerCase().includes(term)) return true;
      // Search in notes
      if (record.notes?.toLowerCase().includes(term)) return true;
      // Search in linked equipment names
      if (record.equipmentIds) {
        return record.equipmentIds.some(eqId => {
          const name = getEquipmentName(eqId);
          return name.toLowerCase().includes(term);
        });
      }
      return false;
    });
  }, [filteredByCategory, searchTerm]);

  // Sort by date (newest first)
  const sortedRecords = useMemo(() => {
    return [...filteredBySearch].sort((a, b) => {
      const dateA = a.completedAt || (a.date ? new Date(a.date).getTime() : 0);
      const dateB = b.completedAt || (b.date ? new Date(b.date).getTime() : 0);
      return dateB - dateA;
    });
  }, [filteredBySearch]);

  // Pagination calculations
  const totalPages = Math.ceil(sortedRecords.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedRecords = sortedRecords.slice(startIndex, endIndex);

  // Reset to page 1 when filters change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, categoryFilter, dateRange, viewMode]);

  // Group records by equipment or job if needed
  const groupedRecords = useMemo(() => {
    if (viewMode === 'all') return null;

    const groups = new Map();

    sortedRecords.forEach(record => {
      if (viewMode === 'byEquipment') {
        // Group by equipment
        const eqIds = record.equipmentIds || [];
        if (eqIds.length === 0) {
          // No equipment linked - group under "Unlinked"
          const key = '_unlinked';
          if (!groups.has(key)) groups.set(key, { name: 'Unlinked Records', records: [] });
          groups.get(key).records.push(record);
        } else {
          eqIds.forEach(eqId => {
            if (!groups.has(eqId)) {
              groups.set(eqId, { name: getEquipmentName(eqId), records: [] });
            }
            groups.get(eqId).records.push(record);
          });
        }
      } else if (viewMode === 'byJob') {
        // Group by job/check type
        const jobKey = record.jobId || record.checkType || record.title || 'Unknown';
        const jobName = record.title || record.checkType || record.equipmentType || 'Unknown Check';
        if (!groups.has(jobKey)) {
          groups.set(jobKey, { name: jobName, records: [] });
        }
        groups.get(jobKey).records.push(record);
      }
    });

    return groups;
  }, [sortedRecords, viewMode]);

  // Calculate counts for tabs
  const recordCount = sortedRecords.length;

  // Export handlers
  const handleExportCSV = () => {
    exportMaintenanceHistoryCSV(sortedRecords, vesselName, {
      getEquipmentName,
      getCrewDisplayName
    });
    setShowExportDropdown(false);
  };

  const handleExportPDF = () => {
    exportMaintenanceHistoryPDF(sortedRecords, vesselName, {
      getEquipmentName,
      getCrewDisplayName
    });
    setShowExportDropdown(false);
  };

  // Handle viewing a completion record
  // FT-349e: pass `scrollToPhotos` flag so the report auto-scrolls to the
  // photos section if this record has photos.
  const handleViewCompletion = (record) => {
    // Enrich record with equipment names for display
    const enrichedRecord = {
      ...record,
      // FT-267 §15.6: prefer stored equipmentNames (FT-221 stability); fall back to register lookup for legacy records
      equipmentNames: record.equipmentNames?.length > 0
        ? record.equipmentNames
        : (record.equipmentIds || []).map(eqId => getEquipmentName(eqId)),
      vesselName: vesselName,
      // FT-349e: request auto-scroll to photos section when opened
      scrollToPhotos: (record.photos?.length || 0) > 0,
    };
    setSelectedCompletion(enrichedRecord);
    setShowCompletionReport(true);
  };

  // Handle editing a completion record (called from MaintenanceCompletionReport)
  // This receives the completion and the edit reason from the modal
  const handleEditCompletion = (completion, reason) => {
    // For now, the edit flow captures the reason but doesn't open an edit form
    // This is Phase 5 - we're setting up the audit trail capture
    // Future: Could open an edit form here
    //
    // For MVP, we just log the intent and the user can manually re-record
    // The EditReasonModal has been shown and the reason captured
    console.log('[MaintenanceHistoryView] Edit requested:', completion.id, 'Reason:', reason);

    // TODO: In a future phase, this could:
    // 1. Open an edit form with the completion data pre-filled
    // 2. On save, call updateCompletionRecord(completion.id, updates, reason)

    // For now, close the report and show a message that editing isn't fully implemented
    // but the audit trail infrastructure is in place
    alert(`Edit reason captured: "${reason}"\n\nNote: Full edit form coming in a future update. The completion record and audit trail infrastructure is now in place.`);
  };

  // FT-402: sign-off status badge for history list entries (mobile card +
  // desktop row). Mirrors the drills pattern (DrillHistoryTab) but reads the
  // maintenance field `signedOffStatus` ('pending' | 'signed_off') instead of
  // the drills `reviewStatus` ('pending' | 'approved'). Returns null for
  // legacy records with no sign-off state so old data renders unchanged.
  //
  // NOTE (future — skipper notifications tab): when the pending queue grows we
  // want a dedicated "Awaiting Sign-Off" view/notification for skippers so
  // they don't have to scan the whole history for amber badges. Note-only; not
  // built here (FT-402 scope is badges + action on this tab).
  const renderSignOffBadge = (record) => {
    if (record.signedOffStatus === 'signed_off') {
      return (
        <span
          data-testid="maintenance-history-sign-off-badge-signed"
          className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700"
        >
          Signed Off
        </span>
      );
    }
    if (record.signedOffStatus === 'pending') {
      return (
        <span
          data-testid="maintenance-history-sign-off-badge-pending"
          className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700"
        >
          Awaiting Sign-Off
        </span>
      );
    }
    return null;
  };

  // Render a mobile card for a record
  const renderMobileCard = (record, index) => {
    const recordDate = record.completedAt || record.date;
    // FT-221: Pass record for performerName check
    const performer = getCrewDisplayName(record.performedBy || record.inspector, record);
    const hasDefects = record.defectsFound || record.hasDefects;
    const title = record.title || record.checkType || record.equipmentType || 'Maintenance Check';
    const type = record.checkType || record.interval || '';

    return (
      <div
        key={record.id || index}
        onClick={() => handleViewCompletion(record)}
        className="border border-gray-200 rounded-lg p-4 bg-white cursor-pointer hover:bg-gray-50 hover:border-cyan-300 transition-colors"
      >
        <div className="flex justify-between items-start mb-2">
          <div>
            <div className="font-medium text-gray-900">
              {title}
              {record.completionType === 'adhoc' && (
                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-700">
                  Ad-hoc
                </span>
              )}
            </div>
            <div className="text-sm text-gray-500">
              {type && `${type} • `}{formatDate(recordDate)}
            </div>
          </div>
          <div className="flex flex-col items-end gap-1">
            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
              hasDefects ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
            }`}>
              {hasDefects ? (
                <><XCircle className="h-3 w-3" /> Fail</>
              ) : (
                <><CheckCircle className="h-3 w-3" /> Pass</>
              )}
            </span>
            {/* FT-402: sign-off status badge */}
            {renderSignOffBadge(record)}
          </div>
        </div>
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center gap-1 text-gray-600">
            <User className="h-3.5 w-3.5" />
            {performer}
          </div>
          <div className="flex items-center gap-3">
            {/* FT-349e: photos indicator inline alongside defect status */}
            <RowStatusIndicator
              hasPhotos={(record.photos?.length || 0) > 0}
              photoCount={record.photos?.length || 0}
              variant="inline"
              size="sm"
              testIdPrefix={`history-mobile-${record.id}`}
            />
            {record.linkedDefectId ? (
              <span className="inline-flex items-center gap-1 text-amber-600 text-xs">
                <AlertTriangle className="h-3 w-3" />
                1 defect {record.defectStatus === 'Open' ? 'open' : 'resolved'}
              </span>
            ) : (
              <span className="text-gray-400 text-xs">0 defects</span>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Render a single record row
  const renderRecordRow = (record, index) => {
    const recordDate = record.completedAt || record.date;
    // FT-221: Pass record for performerName check
    const performer = getCrewDisplayName(record.performedBy || record.inspector, record);
    const hasDefects = record.defectsFound || record.hasDefects;
    const title = record.title || record.checkType || record.equipmentType || 'Maintenance Check';

    return (
      <tr
        key={record.id || index}
        onClick={() => handleViewCompletion(record)}
        className="hover:bg-gray-50 cursor-pointer"
      >
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
          {formatDate(recordDate)}
        </td>
        <td className="px-6 py-4 text-sm text-gray-900">
          <div className="font-medium">
            {title}
            {record.completionType === 'adhoc' && (
              <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-700">
                Ad-hoc
              </span>
            )}
          </div>
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
          {record.checkType || record.interval || '-'}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
          {performer}
        </td>
        <td className="px-6 py-4 whitespace-nowrap">
          <div className="flex flex-col items-start gap-1">
            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
              hasDefects ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
            }`}>
              {hasDefects ? (
                <><XCircle className="h-3 w-3" /> Fail</>
              ) : (
                <><CheckCircle className="h-3 w-3" /> Pass</>
              )}
            </span>
            {/* FT-402: sign-off status badge */}
            {renderSignOffBadge(record)}
          </div>
        </td>
        {/* FT-349e: photos indicator column */}
        <RowStatusIndicator
          hasPhotos={(record.photos?.length || 0) > 0}
          photoCount={record.photos?.length || 0}
          variant="cell"
          size="sm"
          testIdPrefix={`history-row-${record.id}`}
        />
        <td className="px-6 py-4 whitespace-nowrap text-sm">
          {record.linkedDefectId ? (
            <span className="inline-flex items-center gap-1 text-amber-600">
              <AlertTriangle className="h-3 w-3" />
              1 {record.defectStatus === 'Open' ? 'open' : 'resolved'}
            </span>
          ) : (
            <span className="text-gray-400">-</span>
          )}
        </td>
      </tr>
    );
  };

  // Render grouped view
  const renderGroupedView = () => {
    if (!groupedRecords || groupedRecords.size === 0) {
      return (
        <div className="px-6 py-12 text-center text-gray-500">
          No records found matching your filters.
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {Array.from(groupedRecords.entries()).map(([key, group]) => (
          <div key={key} className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="bg-gray-50 px-4 md:px-6 py-3 border-b border-gray-200">
              <h3 className="text-sm font-semibold text-gray-900">
                {group.name}
                <span className="ml-2 text-gray-500 font-normal">({group.records.length} records)</span>
              </h3>
            </div>

            {/* Mobile cards - visible on small screens */}
            <div className="md:hidden p-3 space-y-3">
              {group.records.map((record, idx) => renderMobileCard(record, idx))}
            </div>

            {/* Desktop table - hidden on small screens */}
            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-white">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[13%]">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[28%]">Check</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[13%]">Type</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[15%]">Performed By</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[9%]">Result</th>
                    {/* FT-349e: extras column (photos etc.) */}
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[9%]">Extras</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[13%]">Defects</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {group.records.map((record, idx) => renderRecordRow(record, idx))}
                </tbody>
              </table>
            </div>
          </div>
        ))}
      </div>
    );
  };

  // Render pagination controls
  const renderPagination = () => {
    if (sortedRecords.length <= itemsPerPage) return null;

    return (
      <div className="mt-6 flex items-center justify-between border-t border-gray-200 pt-4">
        {/* Items per page selector */}
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-700">Show:</span>
          <select
            value={itemsPerPage}
            onChange={(e) => {
              setItemsPerPage(Number(e.target.value));
              setCurrentPage(1);
            }}
            className="rounded-md border-gray-300 text-sm px-2 py-1"
          >
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
            <option value={sortedRecords.length}>All</option>
          </select>
          <span className="text-sm text-gray-700">
            Showing {startIndex + 1}-{Math.min(endIndex, sortedRecords.length)} of {sortedRecords.length}
          </span>
        </div>

        {/* Page navigation */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
            className="px-3 py-1 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
          >
            Previous
          </button>

          {/* Page numbers */}
          <div className="flex gap-1">
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter(page => {
                return page === 1 ||
                       page === totalPages ||
                       Math.abs(page - currentPage) <= 2;
              })
              .map((page, idx, arr) => {
                const prevPage = arr[idx - 1];
                const showEllipsis = prevPage && page - prevPage > 1;

                return (
                  <React.Fragment key={page}>
                    {showEllipsis && <span className="px-2 text-gray-400">...</span>}
                    <button
                      onClick={() => setCurrentPage(page)}
                      className={`px-3 py-1 text-sm rounded-md ${
                        currentPage === page
                          ? 'bg-cyan-600 text-white'
                          : 'border border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {page}
                    </button>
                  </React.Fragment>
                );
              })}
          </div>

          <button
            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages}
            className="px-3 py-1 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
          >
            Next
          </button>
        </div>
      </div>
    );
  };

  // Render flat table view
  const renderTableView = () => {
    if (sortedRecords.length === 0) {
      return (
        <div className="px-6 py-12 text-center text-gray-500">
          {history.length === 0
            ? 'No maintenance history recorded yet.'
            : 'No records found matching your filters.'}
        </div>
      );
    }

    return (
      <>
        {/* Mobile cards - visible on small screens */}
        <div className="md:hidden space-y-3">
          {paginatedRecords.map((record, index) => renderMobileCard(record, index))}
        </div>

        {/* Desktop table - hidden on small screens */}
        <div className="hidden md:block overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[13%]">Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[28%]">Equipment / Job</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[13%]">Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[15%]">Performed By</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[9%]">Result</th>
                {/* FT-349e: extras column (photos etc.) */}
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[9%]">Extras</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[13%]">Defects</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {paginatedRecords.map((record, index) => renderRecordRow(record, index))}
            </tbody>
          </table>
        </div>

        {/* Pagination controls */}
        {renderPagination()}
      </>
    );
  };

  // FT-402: keep the open completion in sync with the authoritative history
  // record. After signOffCompletion persists (async setValue), history changes
  // and this effect re-projects the who/when fields onto selectedCompletion so
  // the report's banner + button flip — while preserving the enriched fields
  // (equipmentNames, vesselName, scrollToPhotos) that only live on the selected
  // copy. Guards against wiping the open record when the id is missing.
  React.useEffect(() => {
    if (!selectedCompletion) return;
    const fresh = history.find(r => r.id === selectedCompletion.id);
    if (!fresh) return;
    if (fresh.signedOffStatus === selectedCompletion.signedOffStatus) return;
    setSelectedCompletion(prev => prev ? ({
      ...prev,
      signedOffStatus: fresh.signedOffStatus,
      signedOffBy: fresh.signedOffBy,
      signedOffRole: fresh.signedOffRole,
      signedOffAt: fresh.signedOffAt,
    }) : prev);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history, selectedCompletion?.id, selectedCompletion?.signedOffStatus]);

  // FT-402: sign off the currently-open completion. RBAC is enforced inside the
  // report (button hidden unless can('sign','maintenance')); the persisted
  // signOffCompletion is also skipper-gated at the hook per FT-394. The effect
  // above refreshes the open record once history updates.
  const handleSignOffCompletion = () => {
    if (!selectedCompletion) return;
    signOffCompletion(selectedCompletion.id);
  };

  // Show completion report if active
  if (showCompletionReport && selectedCompletion) {
    return (
      <MaintenanceCompletionReport
        completion={selectedCompletion}
        equipment={equipment}
        // FT-349e: needed by PhotoAttachment in the photos section
        currentVesselId={vessel?.id}
        onBack={() => {
          setShowCompletionReport(false);
          setSelectedCompletion(null);
        }}
        onEdit={handleEditCompletion}
        // FT-402: sign-off action (gated + refreshes selected record)
        onSignOff={handleSignOffCompletion}
      />
    );
  }

  return (
    <div className="rounded-2xl border border-gray-200 shadow-sm bg-white overflow-hidden">
      {/* Header - matches Drills/Training pattern */}
      <div className="bg-slate-700 px-5 py-3 rounded-t-2xl flex items-center justify-between">
        <h3 className="text-base font-semibold text-white whitespace-nowrap">
          Maintenance History
        </h3>
        <div className="flex items-center gap-2">
          {/* Export Dropdown */}
          <div className="relative">
            <button
              onClick={() => setShowExportDropdown(!showExportDropdown)}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-white bg-slate-700 border border-slate-500 rounded-md hover:bg-slate-500"
            >
              <Download className="h-4 w-4" />
              Export
              <ChevronDown className="h-4 w-4" />
            </button>
            {showExportDropdown && (
              <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg border border-gray-200 z-10">
                <button
                  onClick={handleExportCSV}
                  className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                >
                  <Download className="h-4 w-4" />
                  Export as CSV
                  <span className="text-xs text-gray-400 ml-auto">Spreadsheet</span>
                </button>
                <button
                  onClick={handleExportPDF}
                  className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                >
                  <Download className="h-4 w-4" />
                  Export as PDF
                  <span className="text-xs text-gray-400 ml-auto">Print-ready</span>
                </button>
              </div>
            )}
          </div>
          {/* Close Button - only shown when onBack is provided (modal mode) */}
          {onBack && (
            <button
              onClick={onBack}
              className="inline-flex items-center justify-center p-2 text-white hover:text-gray-200 hover:bg-slate-500 rounded-md"
              title="Close"
            >
              <X className="h-5 w-5" />
            </button>
          )}
        </div>
      </div>

      {/* Filter Section - now below header with light background */}
      <div className="px-6 py-5 bg-gray-50 border-b border-gray-200">
        <div className="space-y-3">
          {/* Row 1: Search + Category + Date Range */}
          <div className="flex flex-col sm:flex-row gap-3">
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search equipment or job..."
                className="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 rounded-md focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-cyan-600"
              />
            </div>

            {/* Dropdowns */}
            <div className="flex gap-2">
              <Dropdown
                value={categoryFilter}
                onChange={setCategoryFilter}
                width="w-auto min-w-[140px]"
                // FT-267 §16: the canonical CATEGORY_DEFINITIONS list is now
                // the single source of truth for filter options — replaces
                // the hard-coded array that was inconsistent with what the
                // forms actually stored.
                options={[
                  { value: 'all', label: 'All Categories' },
                  ...CATEGORY_DEFINITIONS.map(d => ({ value: d.value, label: d.label })),
                ]}
              />
              <Dropdown
                value={dateRange}
                onChange={setDateRange}
                width="w-auto min-w-[140px]"
                options={[
                  { value: '30days', label: 'Last 30 days' },
                  { value: '3months', label: 'Last 3 months' },
                  { value: '12months', label: 'Last 12 months' },
                  { value: 'all', label: 'All time' }
                ]}
              />
            </div>
          </div>

          {/* Row 3: FilterTabs + Count */}
          <div className="flex items-center justify-between">
            <FilterTabs
              tabs={[
                { id: 'all', label: 'All History' },
                { id: 'byEquipment', label: 'By Equipment' },
                { id: 'byJob', label: 'By Job' }
              ]}
              activeTab={viewMode}
              onTabChange={setViewMode}
            />
            <span className="text-sm text-gray-500">{recordCount} records</span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {viewMode === 'all' ? renderTableView() : renderGroupedView()}
      </div>
    </div>
  );
};

export default MaintenanceHistoryView;
